<!-- Copyright (c) 2022 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="verification-main">
    <div class="verification-main-content">
      <div class="header">
        <div>{{ title }} - {{ sections[currentSectionIndex]['name'] }}</div>
        <img @click="$emit('update:show', false)" class="close-icon" src="https://dsmlib.logistics.systems/Icons/Cancel.png">
      </div>
      <div class="body">
        <div class="sections-indicator">
          <div class="section-number" :style="sectionStyling(i)" v-for="(s, i) in slotsCount" :key="i">{{ i + 1 }}</div>
        </div>
        <div class="body-content" v-for="(s, i) in sections" :key="i" :style="contentStyling(i)">
          <button class="auto-select-button" @click="selectBestMatch">Auto Select Best Match</button>
          
          <div class="body-details">
            <Carousel class="carousel-container" v-if="attachmentsList.length > 0" :viewCount="attachmentsList.length">
              <div class="attachments-section" v-for="(a, i) in attachmentsList" :key="i" :slot="`content${i + 1}`">
                <object class="attachment-container" v-if="viewableFormat.includes(a['link'].split('.').at(-1).toLowerCase()) || a['link'].startsWith('blob')" :data="a['link']" download="false"></object>
                <div class="attachment-container" v-else>
                  <div>File type <b>{{ a['link'].split('.').at(-1).toLowerCase() }}</b> is not viewable.<br>Please use the download button below to download the attachment</div>
                </div>
                <div v-if="attachmentsList.length > 0" class="attachment-details">
                  <div class="arrow-up" @click="showAttachmentDetails = !showAttachmentDetails">
                    <img src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png" alt="" :style="{ transform: `scaleY(${showAttachmentDetails ? '1' : '-1'})` }">
                  </div>
                  <div>{{ i + 1 }} of {{ attachmentsList.length }} attachment(s)</div>
                  <div><b>Filename</b>: {{ a['filename'] }}</div>
                  <div><b>Remarks</b>: {{ a['remarks'] }}</div>
                  <a :href="a['link']" :target="viewableFormat.includes(a['link'].split('.').at(-1).toLowerCase()) || a['link'].startsWith('blob') ? '_blank' : ''">Download Attachment</a>
                </div>
              </div>
            </Carousel>
            <div v-else class="carousel-container">No attachments available</div>

            <div class="row-sections">
              <div class="row-items row-header">
                <div class="column center-text"></div>
                <div class="column center-text">Submitted</div>
                <div class="column center-text">Selected</div>
                <div class="column center-text">Detected</div>
              </div>

              <div v-for="(d, di) in s['verifyData']" :key="di" class="row-items">
                <div class="column">{{ d['column'] }}</div>
                <div class="column" :style="{ fontStyle: `${d['detected'] ? (d['detected'].split('\n').join('') ? 'normal' : 'italic') : 'italic'}`,
                  color: `${d['detected'] ? (d['detected'].split('\n').join('') ? '' : 'red') : 'red'}` }">{{ d['detected'] ? (d['detected'].split('\n').join('') ? d['detected'] : 'Not detected') : 'Not detected' }}</div>
                <div class="column no-wrap">
                  <InputQuery :placeholder="d['column']" :items="d['submittedList']" :itemKey="d['displayKey']" :descKey="d['submittedDesc']"
                    :isRequired="d['isRequired']" :errorMessage.sync="d['errorMessage']" :selectedObject.sync="d['selectedObject']" @selected="matchedManualSelect($event, di)"
                    :addLink="d['addSubmittedRecordLink'] || ''" :canRefresh="d['canRefreshRecords']" @refreshClicked="submittedInputQueryRefreshClicked" />
                  <div class="score-bubble" v-if="d['selectedObject']" :style="{ backgroundColor: `${d['selectedObject']['matchScore'] > 20 ? 'limegreen' :
                        (d['selectedObject']['matchScore'] > 15 ? 'limegreen' :
                          (d['selectedObject']['matchScore'] > 10 ? 'goldenrod' :
                            d['selectedObject']['matchScore'] ? 'indianred' : 'gray'))}` }">
                    {{ d['selectedObject']['matchScore'] > 20 ? 'Very Strong' :
                        (d['selectedObject']['matchScore'] > 15 ? 'Strong' :
                          (d['selectedObject']['matchScore'] > 10 ? 'Medium' :
                            d['selectedObject']['matchScore'] ? 'Weak' : 'No')) }} Match
                  </div>
                  <div v-if="d['selectedObject'] && d['submittedDetails']">
                    <div v-for="(detail, index) in d['selectedObject'][d['submittedDetails']]" :key="index">
                      <span>{{detail}}</span><br>
                    </div>
                  </div>
                </div>
                <div class="column center-text collision-button">
                  <button @click="openTableBox($event, d)">
                    <div>{{ d['collisions'] ? d['collisions'].length : '0' }} Matches</div>
                    <img src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png" :style="{ transform: `scaleY(${d['showCollisions'] ? '-1' : '1'})` }">
                  </button>
                  <div v-if="d['showCollisions']" class="collision-table" :style="d['tableStyling']">
                    <TableBox :title="d['column']" :tableData="d['collisions']" :additionalButtons="[{ text: 'Select', index: 0, width: 75 }]"
                      :stickColumn="[0]" @buttonClicked="tableButtonClicked(...arguments, di)" :hideColumns="[ 'matchScore' ]" />
                  </div>
                  <div v-if="d['showCollisions']" @click="d['showCollisions'] = false" class="collision-table-background"></div>
                </div>
              </div>
              <slot v-if="i == currentSectionIndex" :name="s['name']"></slot>
            </div>
          </div>
        </div>
      </div>
      <div class="footer">
        <button :disabled="currentSectionIndex == 0" @click="prevNextClicked(false)">Previous</button>
        <button v-if="currentSectionIndex < slotsCount - 1" :disabled="currentSectionIndex == slotsCount - 1" @click="prevNextClicked(true)">Next</button>
        <button v-if="currentSectionIndex == slotsCount - 1" @click="submitClicked">Confirm</button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { doubleMetaphone } from 'double-metaphone';

export default {
  components: {
  },
  data: function() {
    return {
      slotsCount: 0,
      currentSectionIndex: 0,

      showAttachmentDetails: true,
      viewableFormat: [ 'gif', 'jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'svg', 'pdf', 'txt', 'json', 'mp4' ],
    }
  },
  props: {
    title: { type: String, default: 'Title Here' },
    sections: { type: Array, default: function() { return [] } },
    show: { type: Boolean, default: false },
    attachmentsList: { type: Array, default: function() { return [] } },
    showAttachment: { type: Boolean, default: false },
  },
  computed: {
  },
  methods: {
    sectionStyling: function(index) {
      return {
        backgroundColor: index <= this.currentSectionIndex ? '#b3c4ff' : '',
        color: index <= this.currentSectionIndex ? 'white' : '',
        border: `2px solid ${index <= this.currentSectionIndex ? '#b3c4ff' : 'gray'}`
      };
    },
    contentStyling: function(index) {
      return {
        display: this.currentSectionIndex == index ? '' : 'none'
      }
    },

    openTableBox: function(e, item) {
      let rect = e.target.getBoundingClientRect();

      let isOpen = item['showCollisions'];

      this.$nextTick(() => {
        this.sections.forEach(s => {
          s['verifyData'].forEach(v => {
            v['showCollisions'] = false;
          });
        })

        this.$nextTick(() => {
          let top = rect.top + rect.height;
          let right = window.innerWidth - rect.right;

          item['tableStyling'] = {
            top: `${top}px`,
            right: `${right}px`
          };
          item['showCollisions'] = !isOpen;

          if (item['showCollisions']) {
            let winHeight = window.innerHeight / 2;

            if (top + winHeight > window.innerHeight - 10) {
              let diff = (top + winHeight + 10) - (window.innerHeight);
              item['tableStyling'] = {
                top: `${top - diff}px`,
                right: `${right + rect.width}px`
              }
            }
          }
        });
      })
    },

    recalculateIndex: function(ind, length) {
      let newIndex = 0;
      if (ind >= length) {
        newIndex = ind - length;
        while (newIndex >= length) {
          newIndex -= length;
        }
      } else {
        newIndex = ind;
      }

      return newIndex;
    },

    //#region Component functions
    prevNextClicked: function(next) {
       // All required fields filled in before next but on previous it is not required
      if(!next)
        this.currentSectionIndex--;
      else if(this.checkRequiredFields())
        this.currentSectionIndex++;
      else{
        return;
      }
      // check selectedObject is null or undefined
      const currentSection = this.sections[this.currentSectionIndex];
      const selectBestMatch = currentSection['verifyData'].some(v => !v['selectedObject']);
      
      if (selectBestMatch) {
        this.selectBestMatch(); // auto-select best match for the new section
      }
    },
    submitClicked: function() {
      if (this.checkRequiredFields()) { // All required fields filled in
        let detectedData = {};
        let selectedData = {};
        this.sections.forEach(s => {
          s['verifyData'].forEach(v => {
            detectedData[v['historyKey']] = v['detected'].split('\n')[v['compareIndex']];
            if (v['selectedObject']) {
              selectedData[v['historyKey']]  = v['selectedObject'][v['submittedKey']];
            }
          });
        });

        this.$emit('confirmClicked', detectedData, selectedData);
      }
    },
    checkRequiredFields: function() { // return false if one or more required fields is not filled in
      let notSelected = this.sections[this.currentSectionIndex]['verifyData'].filter(v => v['isRequired'] && !v['selectedObject']);
      if (notSelected.length > 0) {
        notSelected.forEach(n => {
          n['errorMessage'] = `${n['column']} is required!`;
        });
      }
      
      return notSelected.length == 0;
    },
    tableButtonClicked: function(text, data, index) {
      let verifyData = this.sections[this.currentSectionIndex]['verifyData'][index];
      verifyData['selectedObject'] = data;
      verifyData['showCollisions'] = false;
      verifyData['errorMessage'] = '';
    },
    selectBestMatch: function() {
      this.sections[this.currentSectionIndex]['verifyData'].forEach(v => {
        if (v['collisions'] && v['collisions'].length > 0) {
          // calculate scores for each collision
          v['collisions'].forEach(collision => {
            let score = 0; 

            // calculate Levenshtein score for submittedKey and submittedDesc with normalized percentage
            let levScoreKey = this.levenshtein(v['detected'], collision[v['submittedKey']]);
            score += (1 - (levScoreKey / Math.max(v['detected'].length, collision[v['submittedKey']].length))) * 10;

            let levScoreDesc = this.levenshtein(v['detected'], collision[v['submittedDesc']]);
            score += (1 - (levScoreDesc / Math.max(v['detected'].length, collision[v['submittedDesc']].length))) * 10;

            // calculate Double Metaphone score for submittedKey then check for primary and secondary matches
            let metaphoneDetected = doubleMetaphone(v['detected']);
            let metaphoneCollisionKey = doubleMetaphone(collision[v['submittedKey']]);
            if (metaphoneDetected[0] === metaphoneCollisionKey[0]) {
              score += 20; // higher score for primary match
            }
            if (metaphoneDetected[1] === metaphoneCollisionKey[1]) {
              score += 10; // additional score for secondary match
            }

            let metaphoneCollisionDesc = doubleMetaphone(collision[v['submittedDesc']]);
            if (metaphoneDetected[0] === metaphoneCollisionDesc[0]) {
              score += 20; 
            }
            if (metaphoneDetected[1] === metaphoneCollisionDesc[1]) {
              score += 10; 
            }

            // Calculate Jaro-Winkler score for submittedKey and submittedDesc with normalized percentage
            // if the destination code is provided will run and add to the score
            if(v['code']) {
              let jaroWinklerScoreKey = this.jaroWinkler(v['code'], collision[v['submittedKey']]);
              score += jaroWinklerScoreKey * 70;
            }else if (v['name']) {
              let jaroWinklerScoreDesc = this.jaroWinkler(v['name'], collision[v['submittedDesc']]);
              score += jaroWinklerScoreDesc * 70;                
            }else{
              let jaroWinklerScoreDesc = this.jaroWinkler(v['detected'], collision[v['submittedDesc']]);
              score += jaroWinklerScoreDesc * 70;
            }

            // store the calculated match score in the collision object
            collision['matchScore'] = score;
          });

          // sort collisions by match score in descending order
          v['collisions'].sort((colA, colB) => parseFloat(colB.matchScore) - parseFloat(colA.matchScore));

          // select the best match (first index 0) with the highest score where already sorted
          v['selectedObject'] = v['collisions'][0];
        }
      });
    },
    //  calculates the Jaro-Winkler distance between two strings (s1, s2).
    //  the distance is a measure of similarity between two strings, with a higher score indicating a closer match.
    jaroWinkler: function(s1, s2) {
      const match = new Set(); 
      const s1Len = s1.length;
      const s2Len = s2.length;
      const maxDist = Math.floor(Math.max(s1Len, s2Len) / 2) - 1; 
      let matches = 0 ; 
      let transpositions = 0; 
      let transIndex = 0;

      // find matches
      for (let i = 0; i < s1Len; i++) {
        for (let j = Math.max(0, i - maxDist); j <= Math.min(s2Len - 1, i + maxDist); j++) {
          if (s1[i] === s2[j] && !match.has(j)) {
            match.add(j); // mark index as matched
            matches++;
            break;
          }
        }
      }

      if (matches === 0) return 0; 

      for (let i = 0; i < s1Len; i++) {
        if (match.has(i)) {
          while (!match.has(transIndex)) {
            transIndex++; // find the next matched index
          }
          if (s1[i] !== s2[transIndex]) {
            transpositions++; 
          }
          transIndex++;
        }
      }

      // calcualte Jaro distance, prefix length for Jaro-Winkler adjustment then Jaro-Winkler distance
      const jaro = (matches / s1Len + matches / s2Len + (matches - transpositions / 2) / matches) / 3;
      const prefixLength = [...s1].slice(0, 4).filter((char, index) => char === s2[index]).length;
      const jaroWinkler = jaro + (prefixLength * 0.1 * (1 - jaro));

      return jaroWinkler; 
    },
    matchedManualSelect: function(data, index) {
      let verifyData = this.sections[this.currentSectionIndex]['verifyData'][index];
      let compareKey = verifyData['submittedKey'];
      let collisions = verifyData['collisions'];

      if (collisions) {
        let hasMatched = collisions.find(c => c[compareKey] == data[compareKey]);
        if (hasMatched) {
          verifyData['selectedObject'] = hasMatched;
        }
      }
    },
    //#endregion

    //#region Sound Encoding
    listSoundex(list) {
      let soundexEncoding = [];
      for(let i = 0; i < list.length; i++) {
        let name = list[i];
        let code = this.soundex(name);
        soundexEncoding.push(code);
      }
      return soundexEncoding;
    },
    soundex(name) {
      let s = [];
      let si = 1;
      let c;
      let mappings = "01230120022455012623010202";
      try {
        s[0] = name[0].toUpperCase();
        for (let j=0; j < name.length; j++) {
          c = (name[j].toUpperCase()).charCodeAt(0) - 65;
          if (c >= 0 && c <= 25) {
            if (mappings[c] != '0') {
              if (mappings[c] != s[si - 1]) {
                s[si] = mappings[c];
                si++;
              }
              if (si > 3) {
                break;
              }
            }
          }
        }
        if (si <= 3) {
          while (si <= 3) {
            s[si] = 0;
            si++;
          }
        }
        s = s.join("");
      }
      catch(err) {
        s = "";
      }
      return s;
    },
    listDMetaphone(list) {
      let dMetaphoneEncoding = [];
      for(let i = 0; i < list.length; i++) {
        let name = list[i];
        let code = doubleMetaphone(name);
        dMetaphoneEncoding.push(code);
      }
      return dMetaphoneEncoding;
    },
    levenshtein(s, t) {
      if (s === t) {
          return 0;
      }
      var n = s.length, m = t.length;
      if (n === 0 || m === 0) {
          return n + m;
      }
      var x = 0, y, a, b, c, d, g, h, k;
      var p = new Array(n);
      for (y = 0; y < n;) {
          p[y] = ++y;
      }

      for (; (x + 3) < m; x += 4) {
          var e1 = t.charCodeAt(x);
          var e2 = t.charCodeAt(x + 1);
          var e3 = t.charCodeAt(x + 2);
          var e4 = t.charCodeAt(x + 3);
          c = x;
          b = x + 1;
          d = x + 2;
          g = x + 3;
          h = x + 4;
          for (y = 0; y < n; y++) {
              k = s.charCodeAt(y);
              a = p[y];
              if (a < c || b < c) {
                  c = (a > b ? b + 1 : a + 1);
              }
              else {
                  if (e1 !== k) {
                      c++;
                  }
              }

              if (c < b || d < b) {
                  b = (c > d ? d + 1 : c + 1);
              }
              else {
                  if (e2 !== k) {
                      b++;
                  }
              }

              if (b < d || g < d) {
                  d = (b > g ? g + 1 : b + 1);
              }
              else {
                  if (e3 !== k) {
                      d++;
                  }
              }

              if (d < g || h < g) {
                  g = (d > h ? h + 1 : d + 1);
              }
              else {
                  if (e4 !== k) {
                      g++;
                  }
              }
              p[y] = h = g;
              g = d;
              d = b;
              b = c;
              c = a;
          }
      }

      for (; x < m;) {
          var e = t.charCodeAt(x);
          c = x;
          d = ++x;
          for (y = 0; y < n; y++) {
              a = p[y];
              if (a < c || d < c) {
                  d = (a > d ? d + 1 : a + 1);
              }
              else {
                  if (e !== s.charCodeAt(y)) {
                      d = c + 1;
                  }
                  else {
                      d = c;
                  }
              }
              p[y] = d;
              c = a;
          }
          h = d;
      }

      return h;
    },
    //#endregion

    submittedInputQueryRefreshClicked: function(placeholder) {
      this.$emit("submittedRefreshClicked", placeholder);
    },
  },
  async mounted() {
    this.slotsCount = Object.keys(this.$slots).length;
    let loginDetails = await this.$apiCallV2('/Login/GetDetails');
    let allHistoricalValue = await this.$apiCallV2(`/UserSelection/GetHistoricalValue?corefname=${loginDetails['CoRefname']}`);
    // console.log('allHistoricalValue', allHistoricalValue);

    this.sections.forEach(s => { // Each sections/pages
      s['verifyData'].forEach(v => { // Each data/rows in the page
        this.$set(v, 'showCollisions', false); // Make sure that the collisions table box is hidden on load - use set to make sure that it is reactive
        this.$set(v, 'tableStyling', {}); // Add table styling for each of the table box - use set to make sure that it is reactive
        this.$set(v, 'errorMessage', ''); // Add error message for the input query - use set to make sure that it is reactive
        this.$set(v, 'selectedObject', null); // Add error message for the input query - use set to make sure that it is reactive


        // let compareKey = v['compareByKey'] ? v['submittedKey'] : v['submittedDesc'];
        // console.log(`${v['column']}: compareByKey ${v['compareByKey']}`);
        let allValue = (
          v['compareByKey'] ? // if compareByKey is true
          v['submittedList'].map(vs => vs[v['submittedKey']]) : // only take all the keys into consideration for comparison
          v['submittedList'].map(vs => vs[v['submittedDesc']]).concat(v['submittedList'].map(vs => vs[v['submittedKey']])) // else, take keys and descriptions for comparison
        );
        // console.log(`${v['column']} allValue`, allValue);
        v['soundexEncoding'] = this.listSoundex(allValue); // All soundex encoding
        // console.log(`${v['column']} soundex`, v['soundexEncoding']);
        v['metaphoneEncoding'] = this.listDMetaphone(allValue); // All double metaphone encoding
        // console.log(`${v['column']} metaphone`, v['metaphoneEncoding']);

        let detected = v['detected'] ? v['detected'].split('\n')[v['compareIndex']] : ''; // Get the first row of the detected
        // console.log(`${v["column"]} detected`, v['detected'], detected);
        if (detected) { // If there is a value from the detected column
          let detectedSoundex = this.soundex(detected); // Detected soundex encoding
          // console.log(`${v["column"]} detectedSoundex`, detectedSoundex);
          let detectedMetaphone = doubleMetaphone(detected); // Detected double metaphone encoding
          // console.log(`${v["column"]} detectedMetaphone`, detectedMetaphone);

          // console.log(`----------START ${v['column']}----------`)

          let levScore = v['soundexEncoding'].map(se => this.levenshtein(detectedSoundex, se)); // Get the levenshtein score
          // console.log(`${v["column"]} levScore`, levScore);
          let zeroDistance = levScore.reduce((a, e, i) => { // Get the zero distance from the levenshtein score
            e == 0 ? a.push(i) : null;
            return a;
          }, []);
          // console.log(`${v["column"]} zeroDistance`, zeroDistance);
          let scoreTally = zeroDistance.reduce((a, b) => { // Get the score tally from the zero distance
            a[b] = 0;
            return a;
          }, {});
          // console.log(`${v["column"]} scoreTally`, scoreTally);

          let firstScore = zeroDistance.map(zd => this.levenshtein(v['metaphoneEncoding'][zd][0], detectedMetaphone[0])); // Get the first score
          // console.log(`${v["column"]} firstScore`, firstScore);
          let firstFinalScore = firstScore.map((n, i) => { // Finalize the first score
            let max = Math.max(detectedMetaphone[0].length, v['metaphoneEncoding'][i][0].length);
            let num = max == 0 ? '1' : max;
            let result = (((num - n) / num) * 5).toPrecision(3);
            return result === '5.00' ? '6.00' : result;
          });
          // console.log(`${v["column"]} firstFinalScore`, firstFinalScore);

          let secondScore = zeroDistance.map(zd => this.levenshtein(v['metaphoneEncoding'][zd][1], detectedMetaphone[1])); // Get the second score
          // console.log(`${v["column"]} secondScore`, secondScore);
          let secondFinalScore = secondScore.map((n, i) => { // Finalize the second score
            let max = Math.max(detectedMetaphone[1].length, v['metaphoneEncoding'][i][1].length);
            let num = max == 0 ? '1' : max;
            let result = (((num - n) / num) * 5).toPrecision(3);
            return result === '5.00' ? '6.00' : result;
          });
          // console.log(`${v["column"]} secondScore`, secondFinalScore);

          let defLevScore = zeroDistance.map(zd => { // Calculate the percentage of score
            let newIndex = this.recalculateIndex(zd, v['submittedList'].length);
            let data = v['submittedList'][newIndex][v['submittedDesc']];
            let score = this.levenshtein(detected, data.toUpperCase());
            let num = Math.max(detected.length, data.length);
            let result = (((num - score) / num) * 10).toPrecision(3);
            return result === '10.0' ? '11.0' : result || 0;
          });
          // console.log(`${v["column"]} defLevScore`, defLevScore);

          let allKeys = Object.keys(scoreTally);
          allKeys.forEach((k, ki) => scoreTally[k] = firstFinalScore[ki] ); // Update score from first final score
          allKeys.forEach((k, ki) => scoreTally[k] = (parseFloat(scoreTally[k]) + parseFloat(secondFinalScore[ki])).toPrecision(3) ); // Update score from second final score
          allKeys.forEach((k, ki) => scoreTally[k] = (parseFloat(scoreTally[k]) + parseFloat(defLevScore[ki])).toPrecision(3) ); // Update score from the percentage

          if (v['compareByKey']) { // if compareByKey is passed
            const keyIndex = allValue.findIndex((value) => { 
              return value.toUpperCase().trim() === detected.toUpperCase().trim(); 
            }); // try to find the index of the key
            if (keyIndex !== -1) { // if index is found
              const scoreAsFloat = parseFloat(scoreTally[keyIndex]);
              scoreTally[keyIndex] = isNaN(scoreAsFloat) ? 100 : scoreAsFloat + 100; // manually inflate the score by 100
            }
          }
          // console.log(`${v["column"]} scoreTally`, scoreTally);

          try { // Update score using previous user selection
            let historicalValue = allHistoricalValue[v['historyKey']]; // Getting the historical value
            if (historicalValue) {
              let historicalValueEntries = Object.entries(historicalValue).find(hv => hv[0] === detected) || []; // Get the entries from the value
              if (historicalValueEntries[1]) {
                Object.keys(historicalValueEntries[1]).map(hve => v['submittedList'].find(sl => sl[v['submittedKey']] === hve)['#']).forEach((k, i) => {
                  scoreTally[k - 1] = parseFloat((scoreTally[k - 1] || 0)) + parseFloat(Object.values(historicalValue[detected])[i]).toPrecision(3);
                });
              }
            }
          } catch (e) { // Any error when updating the score from user selection
            // console.log('Error update score from previous user selection', e.message);
          }

          let scoreTallySorted = Object.entries(scoreTally).sort(([,a], [,b]) => b - a); // Sort the score tally
          // scoreTallySorted = scoreTallySorted.filter(st => parseFloat(st[1]) > 10.0); // Get the score tally after sorted that is more than 10 only
          // console.log(`${v["column"]} scoreTallySorted`, scoreTallySorted);

          let matchedData = scoreTallySorted.map(st => {
            let index = this.recalculateIndex(st[0], v['submittedList'].length);
            return { ...v['submittedList'][index], matchScore: parseFloat(st[1]) }
          }); // Data used in the table box
          // console.log('matchedData', matchedData);
          v['collisions'] = matchedData;

          if (matchedData.length == 1) { // Auto select if there is only 1 match
            v['selectedObject'] = matchedData[0];
          }

          // console.log('----------DONE----------')
        }
      });
    });
    this.selectBestMatch();

    // console.log(this.sections);
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.verification-main {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(128,128,128,0.5);
  z-index: 999;
  padding: 10px 45px;

  .verification-main-content {
    height: 100%;
    width: 100%;
    background-color: white;
    display: flex;
    flex-direction: column;

    > * {
      padding: 5px;
    }

    > .header {
      position: relative;
      padding: 10px 5px;
      border-bottom: 2px solid gray;
      
      > .close-icon {
        height: 20px;
        width: 20px;
        position: absolute;
        right: 5px;
        top: 5px;
        cursor: pointer;
      }
    }

    > .body {
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      > .sections-indicator {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-around;

        > .section-number {
          border-radius: 100%;
          width: 30px;
          height: 30px;
          border: 2px solid gray;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      > .body-content {
        height: 100%;
        margin-top: 5px;
        display: flex;
        flex-direction: column;

        > .auto-select-button {
          align-self: flex-end;
        }

        > .body-details {
          display: flex;
          height: 100%;

          > .carousel-container {
            max-width: 35%;
            min-width: 35%;
            width: 35%;
            border: 1px solid gray;
            margin-right: 5px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          > .row-sections {
            overflow: auto;
            width: 100%;

            > .row-items {
              display: flex;
              align-items: flex-start;
              background-color: rgba(211,211,211,0.5);
              margin: 3px 0;

              > .column {
                position: relative;
                width: 100%;
                min-width: calc((100% - 400px) / 2);
                max-width: calc((100% - 400px) / 2);
                padding: 5px;
                white-space: pre-wrap;

                > .score-bubble {
                  color: white;
                  padding: 3px 5px;
                  border-radius: 5px;
                  width: fit-content;
                  white-space: nowrap;
                }
              }

              > :first-child, > :last-child {
                max-width: 200px;
                min-width: 200px;
              }

              > .collision-button {
                display: flex;
                justify-content: flex-end;
                
                > button {
                  display: flex;
                  align-items: center;
                  position: relative;

                  > * {
                    pointer-events: none;
                  }

                  > img {
                    margin-left: 5px;
                    height: 20px;
                    width: 20px;
                    transition: 0.3s;
                  }
                }

              
              }

              > .center-text {
                text-align: center;
              }

              > .no-wrap {
                // white-space: nowrap;
              }
            }

            > .row-header {
              padding: 5px 0;
            }
          }
        }
      }
    }

    > .footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > * {
        margin-left: 5px;
      }
    }
  }
}

.attachments-section {
  height: 100%;
  min-width: 25%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;

  > .attachment-details {
    width: 100%;
    background-color: white;
    padding: 5px;

    > .arrow-up {
      position: absolute;
      width: 30px;
      height: 20px;
      top: 0;
      left: calc(50% - 15px);
      transform: translateY(-100%);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: white;
      border-color: gray;
      border-width: 1px 1px 0 1px;
      border-style: solid;
      cursor: pointer;
      user-select: none;

      > img {
        height: 100%;
        pointer-events: none;
        transition: 0.3s;
      }
    }
  }

  > .attachment-container {
    object-fit: contain;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    text-align: center;
    max-height: calc(100% - 90px);
  }
}
.collision-table {
  position: fixed;
  z-index: 151;
  width: 50vw;
  height: 50vh;
  padding: 5px;
  background-color: white;
  border: 1px solid gray;
}

> .collision-table-background {
  z-index: 149;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  position: fixed;
}
</style>